import React from 'react';
import { AlertTriangle } from 'lucide-react';

import { Modal } from './Modal';
import { Button } from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  /** Body of the dialog — a sentence explaining what is about to happen. */
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `destructive` shows a red confirm button and warning icon. */
  variant?: 'destructive' | 'primary';
  /** Disables buttons and shows a pending label while the action runs. */
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Small confirmation dialog for destructive or irreversible actions
 * (delete a node, remove a user, revoke a role...). Built on the shared
 * Modal + Button primitives so it inherits theming and dark mode.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'destructive',
  isPending = false,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen onClose={onCancel} size="sm">
      <div className="p-6">
        <div className="flex items-start gap-3">
          {variant === 'destructive' && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/30">
              <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-bold text-title-page-color-100 dark:text-white">
              {title}
            </h3>
            <div className="mt-1.5 text-sm text-title-page-color-300 dark:text-gray-400">
              {message}
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'primary'}
            size="sm"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? '…' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
